import type { store } from '../../store.js'

export type RootState = {
  captions: CaptionsState
  overlay: OverlayState
  loading: LoadingState
  content: ContentState
  deepLink: DeeplinkState
  ui: UIState
  device: DeviceState
  station: StationState
  user: UserState
  search: SearchState
  personalization: PersonalizationState
  screensaver: ScreensaverState
  homeContent: HomeContentState
  error: ErrorState
  multilivestream: MultilivestreamState
  showDetail: ShowDetailState
}
export interface CaptionsState {
  fontSize: string
  fontStyle: string
  captionStyle: string
  fgColor: string
  bgColor: string
  windowColor: string
  fgOpacity: string
  bgOpacity: string
  windowOpacity: string
  textShadowValue: string
  enabled: boolean
}

export interface ContentState {
  analyticsShowTitle: string
  id: string
  videoDetails: {
    assetMezzanine: string
    captionTracks: any[]
    collections: any[]
    content_rating: string
    contentScope: string
    contentType: string
    description_long: string
    duration: number
    episodeTitle: string
    expire_date: string | null
    expired: boolean
    formattedOrdinals: string
    funderMessage: string
    hasRelated: boolean
    is_mvod: boolean | null
    isPlayable: boolean
    isPlayableExplanation: any[]
    legacyTpMediaId: string
    parentShowSlug: string
    relatedAssetUrl: string
    relatedAssetVideoType: string
    seconds_watched: number
    seriesTitle: string | null
    seriesTrackingEvent: string
    short_description: string
    showTitle: string
    url: string | null
    drmWidevideLicenseUrl: string | null
    videoId: string
    trickPlayUrl: string | null
    slug: string
    contentUrl: string
    parent: any
  }
  upNext: {
    upNextShowTitle: string | null
    upNextEpisodeTitle: string | null
    upNextEpisodeNumber: string | null
    upNextSeasonNumber: string | null
    upNextImage: string | null
    upNextVideoType: string | null
    upNextUrl: string | null
  }
  upNextEnabled: boolean
  upNextVideoToggled: boolean
  upNextContinueWatchingTimerThresholdReached: boolean
  upNextContinueWatchingModalVisible: boolean
  upNextContinueWatchingBackUrl: string
}

export interface DeeplinkState {
  message: string
  url: string
  sawSplash: boolean
  deeplinkedTo: boolean | string
}

export interface DeviceState {
  ppid: string
  latEnabled: boolean
  adsEnabled: boolean
  deviceModel: string
  deviceId: string
  UID: string | null
  ttsEnabled: boolean
  distributor: string
  donationQueryParams: string
}

export interface HomeContentState {
  collections: any[] | null
  ready: boolean
}

export interface ScreensaverState {
  screensaverImages: string[] | null
  showing: boolean
  lastInteraction: number
}

export interface LoadingState {
  loadingScreen: boolean
}

export interface OverlayState {
  enabled: boolean
}

export interface PersonalizationState {
  favorites: any | null
  favoritesUrl: string
  favoritesNextUrl: string | null
  favoritesFullyLoaded: boolean
  watchlist: any | null
  watchlistUrl: string
  watchlistNextUrl: string | null
  watchlistFullyLoaded: boolean
  watchHistory: any | null
  watchHistoryUrl: string
  watchHistoryNextUrl: string | null
}

export interface SearchState {
  searchQuery: string
}

export interface StationState {
  call_sign: string
  passportEligible: boolean
  station_id: string
  station_title: string
  black_logo: string // not used
  color_logo: string // not used
  station_scenery: string | null
  white_logo: string // not used
  white_single_logo: string
  white_cobranded_logo: string
  short_common_name: string
  passport_learn_more_url: string
  showMenu: any | null
  donation_campaign: {
    id: string
    minimum_amount: string
    minimum_monthly_amount: string
    monthly_amount_options: any[]
    onetime_amount_options: any[]
    station_has_donation_campaign: boolean
    pre_donation_copy: string
  }
  fallback_station: {
    callsign: string
    cid: string
    city: string
    fullName: string
    shortName: string
  }
  localize_station_step: LocalizeStationStep
}

export enum LocalizeStationStep {
  INIT = 0,
  RELOCALIZING = 1,
  ERROR = 2,
  COMPLETED = 3,
}

export interface UIState {
  side_panel_opened: boolean
  side_panel_visible: boolean
  focusableComponents: {
    screen?: string
  }
  focusedComponent: string
}

export interface UserState {
  pid: string
  memberships: any[]
  validPassportForCurrentStation: boolean
  validPassport: boolean
  first_name: string
  last_name: string
  email: string
  vppa_accepted: boolean
  vppa_last_updated: string
  vppa_status: string
  has_memberships: boolean
  ignoreIsPlayable: boolean
}

export interface ErrorState {
  error: any
  status: string | null
  code: number | null
  content: any | null
  codeBaseLocation: string | null
  errorType: string | null
}

export interface Listing {
  show_tms_id: string
  start_time: string
  duration: number
  show_title: string
  episode_title: string
  show_description: string
  episode_description: string
  listing_image: string
}

export interface Channel {
  feed_cid: string
  non_drm_url: string | null
  drm_dash_url: string | null
  drm_widevine_license_url: string | null
  feed_image: {
    color_logo: string
    white_logo: string
  }
  short_name: string
  full_name: string
  timezone: string
  listings: Listing[]
  logo_overlay: boolean
}

export interface MultilivestreamState {
  isLivestreamAvailable: boolean
  channels: Channel[]
  stationId: string | null
  lastDayFetched: string
}

export type Collection<T> = {
  slug: string
  title: string
  item_type: ItemType
  collection_type?: string
  content: T[]
  url?: string
  meta?: {
    links?: {
      self?: string
      next?: string
      prev?: string
    }
  }
}

export type Availability = 'available' | 'not_yet_available'
export type ResourceType = 'special' | 'franchise' | 'episode'
export type ItemType = // video, skylab-collection, season are videos

    | 'video'
    | 'show'
    | 'season'
    | 'schedule'
    | 'station'
    | 'channel'
    | 'skylab-collection'
    | 'more-extras'
    | 'multiple'
    | 'banner'
export type VideoType = 'full_length' | 'empty_set' | 'Clip' | 'Episode'
export type VideoImages = {
  'asset-mezzanine-16x9'?: string
  'show-mezzanine16x9'?: string
  'show-poster2x3'?: string
  'white-logo-41'?: string
  'black-logo-41'?: string
  'color-logo-41'?: string
  'asset-kids-mezzanine1-16x9'?: string
  background?: string
}

export type ShowImages = {
  'show-logo'?: string
  'show-mezzanine16x9'?: string
  'show-poster2x3'?: string
  'show-banner'?: string
  'show-showcase'?: string
  'show-white-logo'?: string
  'show-black-logo'?: string
  'black-logo-41'?: string
  'color-logo-41'?: string
}

type SeasonImages = {
  'asset-mezzanine-16x9'?: string
}

export type Flags = {
  is_new?: boolean
  is_mvod?: boolean
  has_captions?: boolean
  is_expiring_soon?: boolean
  is_fully_watched?: boolean
  is_playable?: boolean
  is_playable_explanation?: boolean
}

export type Audience = {
  scope: string
  station: any
}

export type Franchise = {
  slug: string
  title: string
  cid: string
  resource_type: string
  funder_message: string
}

export type Link = {
  value: string
  profile: string
  updated_at: string
}

export type Genre = {
  cid: string
  slug: string
  title: string
  url: string
}

export interface Show {
  audience: Audience[]
  cid: string
  display_episode_number: boolean
  franchise: Franchise
  funder_message: string
  resource_type: string
  seasons_count: number
  images: ShowImages
  slug: string
  title: string
  tracking_ga_event: string
  tracking_ga_page: string
  description_long: string
  description_short: string
  title_sortable: string
  item_type: ItemType
  url: string
  links: Link[]
  genre: Genre
}

export interface Season {
  cid: string
  ordinal: number
  resource_type: ResourceType
  show: Show
  title: string
  images: SeasonImages
}

export type Parent = {
  cid: string
  ordinal: number
  resource_type: ResourceType
  season?: Season
  show?: Show
  slug: string
  title: string
}

export type Video = {
  slug: string
  title: string
  title_sortable: string
  duration: number
  description_short: string
  description_long: string
  legacy_tp_media_id: number
  item_type: ItemType
  cid: string
  video_type: VideoType
  premiere_date: string
  encore_date: string
  url: string
  images: VideoImages
  flags: Flags
  availability: Availability
  seconds_watched: number
  expire_date: string
  parent: Parent
}

export type CollectionCardProps = {
  title: string
  slug: string
  item_type: ItemType
  description_short: string
  description_long: string
  images: VideoImages
  url: string
  cid: string
  features: boolean
}

export type Empty = {
  title: string
  empty_set: boolean
  feed_cid: string
}

export type ShowDetailState = {
  collections: Collection<any>[]
  pageDetails: any | null
  continueWatchingData: {
    contentDescription: string
    contentHasSecondsWatched: boolean
    contentSecondsWatched: number | null
    contentDuration: number | null
    contentSeason: number | null
    contentEpisode: number | null
    contentTitle: string
    contentMezzanineImage: string
    contentUrl: string
    contentResourceType: string
  }
}

// TODO: remove this when whole app is migrated to react spatial navigation
declare global {
  interface Window {
    focusedComponent: {
      handleBySpatialNavigation?: boolean
    }
  }
}

export type AppDispatch = typeof store.dispatch
